import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  cn,
} from '@berky/ui'
import { CircleAlert, Info, Megaphone } from 'lucide-react'

import type { HomepageNews, HomepageNewsDraft } from './api'

type DisplayNews = HomepageNews | HomepageNewsDraft

export function HomepageNewsContent({ news }: { news: DisplayNews }) {
  const Icon =
    news.tone === 'Urgent' ? CircleAlert : news.tone === 'Important' ? Megaphone : Info
  const toneClass =
    news.tone === 'Urgent'
      ? 'border-destructive/40 bg-destructive-surface text-destructive-strong'
      : news.tone === 'Important'
        ? 'border-warning bg-warning-surface text-warning-strong'
        : 'border-info bg-info-surface text-info-strong'

  return (
    <div className="space-y-5">
      <div className={cn('flex items-start gap-3 rounded-lg border p-4', toneClass)}>
        <Icon className="mt-0.5 size-5 shrink-0" aria-hidden="true" />
        <div className="min-w-0">
          {news.eyebrow && (
            <p className="mb-1 text-xs font-semibold tracking-wide uppercase">
              {news.eyebrow}
            </p>
          )}
          <p className="font-medium">
            {news.tone === 'Urgent'
              ? 'Dringende Information'
              : news.tone === 'Important'
                ? 'Wichtige Information'
                : 'Information'}
          </p>
        </div>
      </div>

      <div className="space-y-4 text-sm leading-relaxed text-foreground">
        {news.blocks.map((block, index) => {
          if (block.type === 'heading') {
            return (
              <h3 key={index} className="pt-1 text-base font-semibold">
                {block.text}
              </h3>
            )
          }
          if (block.type === 'bulletList') {
            return (
              <ul key={index} className="list-disc space-y-2 pl-5 marker:text-primary">
                {block.items.map((item, itemIndex) => (
                  <li key={`${itemIndex}-${item}`}>{item}</li>
                ))}
              </ul>
            )
          }
          if (block.type === 'highlight') {
            return (
              <p
                key={index}
                className="rounded-md border bg-muted/40 px-4 py-3 font-medium"
              >
                {block.text}
              </p>
            )
          }
          return (
            <p key={index} className="whitespace-pre-line">
              {block.text}
            </p>
          )
        })}
      </div>
    </div>
  )
}

export function HomepageNewsDialog({
  news,
  open,
  onOpenChange,
  preview = false,
}: {
  news: DisplayNews
  open: boolean
  onOpenChange: (open: boolean) => void
  preview?: boolean
}) {
  const externalAction = news.actionUrl?.startsWith('https://')
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[90svh] overflow-y-auto sm:max-w-2xl"
        showCloseButton={false}
      >
        <DialogHeader>
          {preview && (
            <p className="text-xs font-semibold tracking-wide text-muted-foreground uppercase">
              Vorschau
            </p>
          )}
          <DialogTitle className="pr-8 text-xl leading-snug">{news.title}</DialogTitle>
          <DialogDescription>
            {preview
              ? 'So erscheint die News nach der Anmeldung.'
              : 'Neues bei Berky Digital'}
          </DialogDescription>
        </DialogHeader>

        <HomepageNewsContent news={news} />

        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="outline">
              Schließen
            </Button>
          </DialogClose>
          {news.actionLabel && news.actionUrl && (
            <Button asChild>
              <a
                href={news.actionUrl}
                {...(externalAction ? { target: '_blank', rel: 'noreferrer' } : {})}
              >
                {news.actionLabel}
              </a>
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
